import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { VolumeCalculator } from './VolumeCalculator';
import { PriceCalculator } from './PriceCalculator';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
    Dialog, DialogContent, DialogTitle,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Calendar } from '../ui/calendar';
import { disablePastDates, makeDisableBeforeDateChargement } from '../../lib/dateConstraints';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { User, MapPin, Truck, CheckCircle2, Globe, Calendar as CalendarIcon, Loader2, ClipboardList, Warehouse, FileText, Sparkles, CircleDot } from 'lucide-react';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useEditLock } from '../../hooks/useEditLock';
import { LockBanner } from '../permissions/LockBanner';
import { ReadOnlyGuard } from '../permissions/ReadOnlyGuard';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export function InventaireFormContent({ inventaire, onSuccess, onCancel, config }) {
    const isEditMode = !!inventaire?.id;
    const lock = useEditLock('inventaire', inventaire?.id, isEditMode);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isCorrecting, setIsCorrecting] = useState(false);

    const [formData, setFormData] = useState({
        client_nom: '', telephone: '', email: '', source: '', departement: '', remarques: '',
        commercial_id: '',
        type_inventaire: '',
        statut: '',
        formules: [],
        garde_meuble: null,
        garde_meuble_tarif: '',
        garde_meuble_duree: '',
        cave_garage: null,
        cave_garage_detail: '',
        remarques_inventaire: '',
        remarques_devis: '',
        adresse_depart: { date: null, adresse: '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null },
        adresse_arrivee: { date: null, adresse: '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null }
    });

    const adresseDepartRef = useRef(null);
    const adresseArriveeRef = useRef(null);
    const acDepartRef = useRef(null);
    const acArriveeRef = useRef(null);
    // Ref vers le PriceCalculator pour forcer la sauvegarde du calcul de prix
    // (debounce 500 ms) avant la fermeture / Enregistrer / génération PDF.
    const priceCalcRef = useRef(null);

    const [initialSnapshot, setInitialSnapshot] = useState(null);
    // Drapeau remonté par le PriceCalculator pour savoir si le calcul de prix est complet.
    // Initialisé à partir du champ calcul_prix chargé sur l'inventaire (si présent).
    const [priceCalcComplete, setPriceCalcComplete] = useState(() => {
        const cp = inventaire?.calcul_prix;
        return !!(cp && (cp.formules_data && Object.keys(cp.formules_data).length > 0));
    });

    useEffect(() => {
        if (inventaire) {
            const normalizeAddr = (a) => {
                if (!a) return { date: null, adresse: '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null };
                let normalDate = null;
                if (a.date) {
                    // Conserve la date en YYYY-MM-DD sans passer par new Date()/toISOString()
                    // pour éviter tout décalage de fuseau horaire (ex: "2026-05-03T00:00:00" parsé en
                    // heure locale France GMT+2 puis .toISOString() renvoie "2026-05-02").
                    const s = String(a.date);
                    normalDate = s.length >= 10 ? s.slice(0, 10) : s;
                }
                return {
                    date: normalDate,
                    adresse: a.adresse || '',
                    etage: a.etage || '',
                    ascenseur: a.ascenseur || '',
                    portage: a.portage || '',
                    autorisation_stationnement: a.autorisation_stationnement ?? null,
                    monte_meuble: a.monte_meuble ?? null
                };
            };
            const data = {
                client_nom: inventaire.client_nom || '',
                telephone: inventaire.telephone || '',
                email: inventaire.email || '',
                source: inventaire.source || '',
                departement: inventaire.departement || '',
                remarques: inventaire.remarques || '',
                commercial_id: inventaire.commercial_id || '',
                type_inventaire: inventaire.type_inventaire || '',
                statut: inventaire.statut || '',
                formules: inventaire.formules || [],
                garde_meuble: inventaire.garde_meuble ?? null,
                garde_meuble_tarif: inventaire.garde_meuble_tarif || '',
                garde_meuble_duree: inventaire.garde_meuble_duree || '',
                cave_garage: inventaire.cave_garage ?? null,
                cave_garage_detail: inventaire.cave_garage_detail || '',
                remarques_inventaire: inventaire.remarques_inventaire || '',
                remarques_devis: inventaire.remarques_devis || '',
                adresse_depart: normalizeAddr(inventaire.adresse_depart),
                adresse_arrivee: normalizeAddr(inventaire.adresse_arrivee),
                calcul_prix: inventaire.calcul_prix || null
            };
            setFormData(data);
            // Delay snapshot to capture state after Select components stabilize
            setTimeout(() => setInitialSnapshot(JSON.stringify(data)), 100);
        }
    }, [inventaire]);

    // Load Google Maps script if needed
    useEffect(() => {
        if (window.google?.maps?.places || !GOOGLE_MAPS_KEY) return;
        if (document.querySelector('script[src*="maps.googleapis.com"]')) return;
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places&language=fr`;
        script.async = true;
        document.head.appendChild(script);
    }, []);

    // Google Places autocomplete
    useEffect(() => {
        const trySetup = () => {
            if (!window.google?.maps?.places) return false;
            const setupAC = (ref, acRef, field) => {
                if (!ref.current || acRef.current) return;
                const ac = new window.google.maps.places.Autocomplete(ref.current, { componentRestrictions: { country: 'fr' }, fields: ['formatted_address'] });
                ac.addListener('place_changed', () => {
                    const place = ac.getPlace();
                    if (place.formatted_address) {
                        setFormData(prev => ({ ...prev, [field]: { ...prev[field], adresse: place.formatted_address } }));
                    }
                });
                acRef.current = ac;
            };
            setupAC(adresseDepartRef, acDepartRef, 'adresse_depart');
            setupAC(adresseArriveeRef, acArriveeRef, 'adresse_arrivee');
            return true;
        };
        // Try immediately, then poll until loaded
        if (trySetup()) return;
        const interval = setInterval(() => {
            if (trySetup()) clearInterval(interval);
        }, 500);
        return () => clearInterval(interval);
    }, []);

    const hasChanges = () => {
        if (!initialSnapshot) return false;
        return JSON.stringify(formData) !== initialSnapshot;
    };

    const handleCancel = () => {
        if (hasChanges()) { setShowCancelConfirm(true); } else { onCancel(); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            // Force la sauvegarde immédiate du calcul de prix (sinon le debounce
            // de 500 ms peut perdre les dernières modifs)
            if (priceCalcRef.current?.flushSave) {
                try { await priceCalcRef.current.flushSave(); } catch { /* on continue */ }
            }
            // Only send changed fields to avoid overwriting data
            const payload = {};
            const snap = initialSnapshot ? JSON.parse(initialSnapshot) : {};
            for (const key of Object.keys(formData)) {
                if (key === 'calcul_prix') continue; // Don't send calcul_prix from form
                const current = JSON.stringify(formData[key]);
                const original = JSON.stringify(snap[key]);
                if (current !== original) {
                    payload[key] = formData[key];
                }
            }
            await axios.put(`${API}/api/inventaires/${inventaire.id}`, payload, { withCredentials: true });
            toast.success('Inventaire mis à jour');
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally { setIsSubmitting(false); }
    };

    const saveAndClose = async () => {
        setIsSubmitting(true);
        try {
            if (priceCalcRef.current?.flushSave) {
                try { await priceCalcRef.current.flushSave(); } catch { /* on continue */ }
            }
            const payload = {};
            const snap = initialSnapshot ? JSON.parse(initialSnapshot) : {};
            for (const key of Object.keys(formData)) {
                if (key === 'calcul_prix') continue;
                const current = JSON.stringify(formData[key]);
                const original = JSON.stringify(snap[key]);
                if (current !== original) {
                    payload[key] = formData[key];
                }
            }
            await axios.put(`${API}/api/inventaires/${inventaire.id}`, payload, { withCredentials: true });
            toast.success('Inventaire mis à jour');
            setShowCancelConfirm(false);
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
            setShowCancelConfirm(false);
        } finally { setIsSubmitting(false); }
    };

    const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
    const updateAddress = (type, field, value) => setFormData(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));

    const correctText = async () => {
        if (!formData.remarques_inventaire?.trim()) return;
        setIsCorrecting(true);
        try {
            const res = await axios.post(`${API}/api/ai/correct-text`, { text: formData.remarques_inventaire }, { withCredentials: true });
            setFormData(prev => ({ ...prev, remarques_inventaire: res.data.corrected_text }));
            toast.success('Texte corrigé');
        } catch (err) {
            toast.error('Erreur lors de la correction');
        } finally { setIsCorrecting(false); }
    };

    const [isCorrectingDevis, setIsCorrectingDevis] = useState(false);
    const correctTextDevis = async () => {
        if (!formData.remarques_devis?.trim()) return;
        setIsCorrectingDevis(true);
        try {
            const res = await axios.post(`${API}/api/ai/correct-text`, { text: formData.remarques_devis }, { withCredentials: true });
            setFormData(prev => ({ ...prev, remarques_devis: res.data.corrected_text }));
            toast.success('Texte corrigé');
        } catch (err) {
            toast.error('Erreur lors de la correction');
        } finally { setIsCorrectingDevis(false); }
    };

    const isSectionComplete = {
        infos: () => !!formData.client_nom && !!formData.telephone && !!formData.email,
        source: () => !!formData.source && !!formData.departement,
        adresseDepart: () => { const a = formData.adresse_depart; return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null; },
        adresseArrivee: () => { const a = formData.adresse_arrivee; return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null; },
        remarques: () => !!formData.remarques,
        typeCommercial: () => !!formData.type_inventaire && !!formData.commercial_id,
        formules: () => formData.formules && formData.formules.length > 0,
        gardeMeuble: () => formData.garde_meuble === false || (formData.garde_meuble === true && !!formData.garde_meuble_tarif && !!formData.garde_meuble_duree),
        caveGarage: () => formData.cave_garage === false || (formData.cave_garage === true && !!formData.cave_garage_detail),
        remarquesInventaire: () => !!formData.remarques_inventaire,
        calculPrix: () => priceCalcComplete,
    };

    const computedStatut = (() => {
        const allComplete = isSectionComplete.typeCommercial() && isSectionComplete.infos() && isSectionComplete.source() && isSectionComplete.adresseDepart() && isSectionComplete.adresseArrivee() && isSectionComplete.formules() && isSectionComplete.gardeMeuble() && isSectionComplete.caveGarage() && isSectionComplete.remarquesInventaire() && isSectionComplete.calculPrix();
        if (allComplete) return 'termine';
        // Une fois édité ou déjà passé par "termine", on reste en "en_redaction"
        if (inventaire?.has_been_edited || inventaire?.statut === 'termine' || inventaire?.statut === 'en_redaction') {
            return 'en_redaction';
        }
        return 'nouveau';
    })();

    const STATUT_STYLES = {
        nouveau: { label: 'Nouveau', color: 'bg-slate-100 text-slate-700 border-slate-300' },
        en_redaction: { label: 'En rédaction', color: 'bg-amber-100 text-amber-700 border-amber-300' },
        termine: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    };

    const SectionLine = ({ isComplete }) => (
        <div className={`w-1 rounded-full self-stretch shrink-0 ${isComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
    );

    const SectionCheck = ({ isComplete }) => (
        isComplete ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : null
    );

    const DatePickerField = ({ label, value, onChange, disabledFn }) => {
        // Parse YYYY-MM-DD en date locale (jour/mois/année) pour éviter tout décalage fuseau
        const dateValue = value ? (() => {
            const s = String(value).slice(0, 10);
            const [y, m, d] = s.split('-').map(Number);
            return (y && m && d) ? new Date(y, m - 1, d) : null;
        })() : null;
        // Formate une Date JS locale en YYYY-MM-DD (sans toISOString qui convertit en UTC)
        const toLocalISO = (d) => {
            if (!d) return null;
            const y = d.getFullYear();
            const mo = String(d.getMonth() + 1).padStart(2, '0');
            const da = String(d.getDate()).padStart(2, '0');
            return `${y}-${mo}-${da}`;
        };
        return (
            <div className="flex items-center gap-4">
                <Label className="text-slate-600 w-28 md:w-72 shrink-0">{label}</Label>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="max-w-sm ml-auto w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateValue ? format(dateValue, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner une date'}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[60]" align="start">
                        <Calendar mode="single" selected={dateValue} onSelect={(d) => onChange(toLocalISO(d))} locale={fr} disabled={disabledFn || disablePastDates} />
                    </PopoverContent>
                </Popover>
            </div>
        );
    };

    const renderAddressForm = (type, title, Icon, inputRef) => {
        const addr = formData[type];
        const isComplete = type === 'adresse_depart' ? isSectionComplete.adresseDepart() : isSectionComplete.adresseArrivee();
        return (
            <Card className="border-slate-200">
                <CardHeader className="pb-3 border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                        <Icon size={16} className="text-[#2e7cd6]" />{title} <SectionCheck isComplete={isComplete} />
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                    <div className="flex gap-4">
                        <SectionLine isComplete={isComplete} />
                        <div className="flex-1 space-y-4">
                            <DatePickerField
                                label={type === 'adresse_depart' ? 'Date de chargement' : 'Date de déchargement'}
                                value={addr.date}
                                onChange={(val) => updateAddress(type, 'date', val)}
                                disabledFn={
                                    type === 'adresse_arrivee'
                                        ? makeDisableBeforeDateChargement(formData.adresse_depart?.date)
                                        : disablePastDates
                                }
                            />
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Adresse</Label>
                                <Input ref={inputRef} className="max-w-sm ml-auto" value={addr.adresse} onChange={e => updateAddress(type, 'adresse', e.target.value)} placeholder="Saisir l'adresse complète" />
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Étage</Label>
                                <Input className="max-w-sm ml-auto" value={addr.etage} onChange={e => updateAddress(type, 'etage', e.target.value)} placeholder="Ex: 3ème" />
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Portage</Label>
                                <Input className="max-w-sm ml-auto" value={addr.portage} onChange={e => updateAddress(type, 'portage', e.target.value)} placeholder="Ex: 20m" />
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Ascenseur</Label>
                                <Select key={`asc-${type}-${addr.ascenseur}`} value={addr.ascenseur || undefined} onValueChange={v => updateAddress(type, 'ascenseur', v)}>
                                    <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent>{config?.ascenseur_options?.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Autorisation stationnement</Label>
                                <Select key={`as-${type}-${addr.autorisation_stationnement}`} value={addr.autorisation_stationnement === true ? 'oui' : addr.autorisation_stationnement === false ? 'non' : undefined} onValueChange={v => updateAddress(type, 'autorisation_stationnement', v === 'oui')}>
                                    <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Monte-meuble</Label>
                                <Select key={`mm-${type}-${addr.monte_meuble}`} value={addr.monte_meuble === true ? 'oui' : addr.monte_meuble === false ? 'non' : undefined} onValueChange={v => updateAddress(type, 'monte_meuble', v === 'oui')}>
                                    <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                    <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    if (!config) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
            </div>
        );
    }

    return (
        <>
            {lock.blocked && <LockBanner userName={lock.user_name} expiresAt={lock.expires_at} onClose={onCancel} />}
            <ReadOnlyGuard module="inventaires" lockBlocked={lock.blocked}>
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <h2 className="text-base md:text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Modifier l'inventaire</h2>
                        <p className="text-sm text-slate-500">Modifiez les informations de l'inventaire</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button type="button" variant="outline" onClick={handleCancel} data-testid="inventaire-form-cancel-btn">
                            Annuler
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" data-testid="inventaire-form-save-btn">
                            {isSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                            Enregistrer
                        </Button>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4">
                <div className="space-y-6">

                {/* Statut inventaire - automatique */}
                <Card className="bg-blue-50 border-2 border-blue-200">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                            <CircleDot size={16} />
                            Statut de l'inventaire
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-semibold border ${STATUT_STYLES[computedStatut]?.color || STATUT_STYLES.nouveau.color}`} data-testid="inventaire-statut-badge">
                            {STATUT_STYLES[computedStatut]?.label || 'Nouveau'}
                        </span>
                    </CardContent>
                </Card>

                {/* 1. Type et Commercial */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <ClipboardList size={16} className="text-[#2e7cd6]" />1. Type et Commercial <SectionCheck isComplete={isSectionComplete.typeCommercial()} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                        <SectionLine isComplete={isSectionComplete.typeCommercial()} />
                        <div className="flex-1 space-y-4">
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Type d'inventaire</Label>
                                <Select key={`type-${formData.type_inventaire}`} value={formData.type_inventaire || '_none'} onValueChange={(v) => updateField('type_inventaire', v === '_none' ? '' : v)}>
                                    <SelectTrigger className="max-w-sm ml-auto" data-testid="inventaire-type-select">
                                        <SelectValue placeholder="Sélectionner un type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none">— Non défini —</SelectItem>
                                        {config?.types?.map(t => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-4">
                                <Label className="text-slate-600 w-28 md:w-72 shrink-0">Commercial</Label>
                                <Select key={`commercial-${formData.commercial_id}`} value={formData.commercial_id || '_none'} onValueChange={(v) => updateField('commercial_id', v === '_none' ? '' : v)}>
                                    <SelectTrigger className="max-w-sm ml-auto" data-testid="inventaire-commercial-select">
                                        <SelectValue placeholder="Sélectionner un commercial" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none">— Non assigné —</SelectItem>
                                        {config?.commerciaux?.map(c => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.couleur && <span className="w-2.5 h-2.5 rounded-full mr-1.5 shrink-0 inline-block" style={{ backgroundColor: c.couleur }} />}
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 2. Infos générales */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <User size={16} className="text-[#2e7cd6]" />2. Informations générales <SectionCheck isComplete={isSectionComplete.infos()} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={isSectionComplete.infos()} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Nom *</Label>
                                    <Input className="max-w-sm ml-auto" value={formData.client_nom} onChange={e => updateField('client_nom', e.target.value)} placeholder="Nom du client" />
                                </div>
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Téléphone</Label>
                                    <Input className="max-w-sm ml-auto" value={formData.telephone} onChange={e => updateField('telephone', e.target.value)} placeholder="06 00 00 00 00" />
                                </div>
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Email</Label>
                                    <Input className="max-w-sm ml-auto" type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="email@exemple.fr" />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 2. Source & Département */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Globe size={16} className="text-[#2e7cd6]" />3. Source & Département <SectionCheck isComplete={isSectionComplete.source()} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={isSectionComplete.source()} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Source</Label>
                                    <Select key={`source-${formData.source}`} value={formData.source || undefined} onValueChange={v => updateField('source', v)}>
                                        <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner une source" /></SelectTrigger>
                                        <SelectContent>{config?.sources?.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Département</Label>
                                    <Select key={`dept-${formData.departement}`} value={formData.departement || undefined} onValueChange={v => updateField('departement', v)}>
                                        <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner un département" /></SelectTrigger>
                                        <SelectContent>{config?.departements?.map(d => <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 4. Adresse de départ */}
                {renderAddressForm('adresse_depart', "4. Adresse de départ", MapPin, adresseDepartRef)}

                {/* 5. Adresse d'arrivée */}
                {renderAddressForm('adresse_arrivee', "5. Adresse d'arrivée", Truck, adresseArriveeRef)}

                {/* 6. Formule */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <ClipboardList size={16} className="text-[#2e7cd6]" />6. Formule <SectionCheck isComplete={formData.formules.length > 0} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={formData.formules.length > 0} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-start gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0 pt-1">Formule</Label>
                                    <div className="max-w-sm ml-auto grid grid-cols-2 gap-x-6 gap-y-2">
                                        {config?.formules?.map(f => {
                                            const checked = formData.formules.includes(f.value);
                                            return (
                                                <label key={f.value} className="flex items-center gap-3 cursor-pointer py-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                formules: checked
                                                                    ? prev.formules.filter(v => v !== f.value)
                                                                    : [...prev.formules, f.value]
                                                            }));
                                                        }}
                                                        className="h-4 w-4 rounded border-slate-300 text-[#2e7cd6] focus:ring-[#2e7cd6]"
                                                    />
                                                    <span className="text-sm text-slate-700">{f.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 6. Garde-meuble */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Warehouse size={16} className="text-[#2e7cd6]" />7. Garde-meuble <SectionCheck isComplete={formData.garde_meuble === false || (formData.garde_meuble === true && !!formData.garde_meuble_tarif && !!formData.garde_meuble_duree)} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={formData.garde_meuble === false || (formData.garde_meuble === true && !!formData.garde_meuble_tarif && !!formData.garde_meuble_duree)} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Garde-meuble</Label>
                                    <Select value={formData.garde_meuble === true ? 'oui' : formData.garde_meuble === false ? 'non' : ''} onValueChange={v => {
                                        if (v !== 'oui' && v !== 'non') return; // Radix Select émet '' au montage → ne pas écraser la valeur
                                        const val = v === 'oui';
                                        setFormData(prev => ({
                                            ...prev,
                                            garde_meuble: val,
                                            garde_meuble_tarif: val ? prev.garde_meuble_tarif : '',
                                            garde_meuble_duree: val ? prev.garde_meuble_duree : ''
                                        }));
                                    }}>
                                        <SelectTrigger className="max-w-sm ml-auto"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="oui">Oui</SelectItem>
                                            <SelectItem value="non">Non</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {formData.garde_meuble === true && (
                                    <>
                                        <div className="flex items-center gap-4">
                                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Mensualité (tarif)</Label>
                                            <Input className="max-w-sm ml-auto" value={formData.garde_meuble_tarif} onChange={e => updateField('garde_meuble_tarif', e.target.value)} placeholder="Ex: 150€/mois" />
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <Label className="text-slate-600 w-28 md:w-72 shrink-0">Durée</Label>
                                            <Input className="max-w-sm ml-auto" value={formData.garde_meuble_duree} onChange={e => updateField('garde_meuble_duree', e.target.value)} placeholder="Ex: 3 mois" />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 7b. Cave / Garage */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Warehouse size={16} className="text-[#2e7cd6]" />8. Cave / Garage <SectionCheck isComplete={formData.cave_garage === false || (formData.cave_garage === true && !!formData.cave_garage_detail)} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={formData.cave_garage === false || (formData.cave_garage === true && !!formData.cave_garage_detail)} />
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center gap-4">
                                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Cave / Garage</Label>
                                    <Select value={formData.cave_garage === true ? 'oui' : formData.cave_garage === false ? 'non' : ''} onValueChange={v => {
                                        if (v !== 'oui' && v !== 'non') return; // Radix Select émet '' au montage → ne pas écraser la valeur
                                        const val = v === 'oui';
                                        setFormData(prev => ({
                                            ...prev,
                                            cave_garage: val,
                                            cave_garage_detail: val ? prev.cave_garage_detail : ''
                                        }));
                                    }}>
                                        <SelectTrigger className="max-w-sm ml-auto" data-testid="cave-garage-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="oui">Oui</SelectItem>
                                            <SelectItem value="non">Non</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {formData.cave_garage === true && (
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Détail</Label>
                                        <Input className="max-w-sm ml-auto" value={formData.cave_garage_detail} onChange={e => updateField('cave_garage_detail', e.target.value)} placeholder="Ex: au -1 volume 5 m³" data-testid="cave-garage-detail" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 7. Remarques inventaire */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <FileText size={16} className="text-[#2e7cd6]" />8. Remarques inventaire <SectionCheck isComplete={!!formData.remarques_inventaire} />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={correctText}
                                disabled={isCorrecting || !formData.remarques_inventaire?.trim()}
                                className="gap-2 ml-auto border-[#2e7cd6] text-[#2e7cd6] hover:bg-[#2e7cd6]/10"
                                data-testid="correct-text-btn"
                            >
                                {isCorrecting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                {isCorrecting ? 'Correction...' : 'Corriger'}
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={!!formData.remarques_inventaire} />
                            <div className="flex-1">
                                <Textarea
                                    value={formData.remarques_inventaire}
                                    onChange={e => updateField('remarques_inventaire', e.target.value)}
                                    placeholder="Notes et remarques..."
                                    rows={6}
                                    className="w-full min-h-[120px] resize-y"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 9. Calculateur de volume */}
                <VolumeCalculator inventaireId={inventaire.id} />

                {/* 10. Calcul de prix */}
                <PriceCalculator ref={priceCalcRef} inventaireId={inventaire.id} formules={formData.formules} adresseDepart={formData.adresse_depart} adresseArrivee={formData.adresse_arrivee} onCompleteChange={setPriceCalcComplete} />
                </div>
            </form>

            <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
                        <AlertDialogDescription>Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de quitter ?</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => {
                            setShowCancelConfirm(false);
                            onCancel();
                        }}>
                            Ne pas enregistrer
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={saveAndClose}>
                            Enregistrer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            </ReadOnlyGuard>
        </>
    );
}
